/* @flow */
import Favicon from 'lib/Favicon';
import Store from 'lib/Store';
import Unread from 'entities/Unread';
import UnreadActions from 'actions/UnreadActions';
import Unreads from 'collections/Unreads';
import ChatActions from 'chat/actions/ChatActions';
import { ActionTypes as ChatActionTypes } from 'chat/constants/ChatConstants';
import { ActionTypes } from 'constants/UnreadConstants';
import { PushEvents } from 'pusher/PusherConstants';
import { chatMessageFactory, shouldListenToPushEventFrom } from 'entities/ChatMessage';

class UnreadStore extends Store {
  getInitialState() {
    return {
      unreads: new Unreads(),
    };
  }

  getUnreads() {
    return this.state.unreads || new Unreads();
  }

  getTotalUnread() {
    return this.getUnreadCount('chats');
  }

  getUnreadCount(id) {
    const unread = this.state.unreads.get(id);
    if (unread) {
      // If a parent unread has been set as 0, then all the children count as 0 implicitly
      let iter = unread;
      while (iter.hasParent()) {
        iter = this.state.unreads.get(iter.getParentId());
        if (!iter || iter.getUnreadCount() === 0) {
          return 0;
        }
      }
      return unread.getUnreadCount();
    } else {
      return 0;
    }
  }

  incrementUnreads(id, parent) {
    let unread = this.state.unreads.get(id);
    if (!unread) {
      this.addNewUnread(new Unread({ id, parent }));
      unread = this.state.unreads.get(id);
    }
    let iter = unread;
    while (iter) {
      iter.incrementUnreadCount();
      iter = this.state.unreads.get(iter.getParentId());
    }
  }

  decrementUnreads(id, count) {
    let iter = this.state.unreads.get(id);
    while (iter) {
      iter.decrementUnreadCountBy(count);
      iter = this.state.unreads.get(iter.getParentId());
    }
  }

  setAsRead(id) {
    const unread = this.state.unreads.get(id);
    if (unread) {
      const numUnread = unread.getUnreadCount();
      let iter = unread;
      while (iter) {
        iter.decrementUnreadCountBy(numUnread);
        iter = this.state.unreads.get(iter.getParentId());
      }
    }
  }

  addNewUnread(unread) {
    this.state.unreads.add(unread);
  }

  messageIsForCurrentChatThread(chatId) {
    return window.location.pathname.replace(/\/+$/, '').endsWith(chatId);
  }

  refreshFavicon() {
    Favicon.showNumber(this.getTotalUnread());
  }

  handleDispatch(action: Object): void {
    switch (action.type) {
      case ChatActionTypes.LIST_CHATS:
        action.chats.forEach((chat) => this.addNewUnread({
          id: chat.id,
          parent: 'chats',
          unreadCount: chat.getUnreadMessages(),
        }));
        this.refreshFavicon();
        this.emitChange();
        break;
      case ChatActionTypes.MARK_READ:
      case ChatActionTypes.READ_MESSAGE:
        this.setAsRead(action.chatId);
        this.refreshFavicon();
        this.emitChange();
        break;
      case ActionTypes.LIST_UNREADS:
        // action.unreads contains a complete description of all unreads, so we reset the state
        this.state = this.getInitialState();

        // NOTE: Temp fix because backend isn't providing this in the initial request
        if (!this.state.unreads.get('chats')) {
          this.addNewUnread(new Unread({ id: 'chats', parent: 'user', unreadCount: 0 }));
        }

        action.unreads.forEach((unread) => this.addNewUnread(unread));
        this.refreshFavicon();
        this.emitChange();
        break;
      case PushEvents.CHANGESET_CREATE_CHAT_MESSAGE: {
        const messages = action.changes.map(change => chatMessageFactory(change.content));

        // Structures to avoid useless update & duplicate
        // (eg only mark "read up to" for the last message per chat).
        const chatToMarkReadUpTo = new Map();
        const chatToIncrementUnread = new Set();

        /* For each received message:
         * If the message is in the current thread, mark it a read.
         * Otherwise load & increment the unread for the chat.
         */
        messages.forEach(message => {
          const chatId = message.getChatId();

          if (!shouldListenToPushEventFrom(message) || message.getSender().isCurrentUser()) {
            return;
          }

          if (this.messageIsForCurrentChatThread(chatId)) {
            const seq = chatToMarkReadUpTo.get(chatId);
            if (!seq || message.getSeq() > seq) {
              chatToMarkReadUpTo.set(chatId, message.getSeq());
            }
          } else {
            chatToIncrementUnread.add(chatId);
          }
        });

        chatToMarkReadUpTo.forEach((seqNumber, chatId) => {
          setTimeout(() => ChatActions.markReadUpto(chatId, seqNumber), 0);
        });

        chatToIncrementUnread.forEach(chatId => {
          this.incrementUnreads(chatId, 'chats');
        });

        if (chatToIncrementUnread.size > 0) {
          UnreadActions.loadUnreads();
          this.refreshFavicon();
          this.emitChange();
        }

        break;
      }
      case PushEvents.CHANGESET_UPDATE_GROUP:
      case PushEvents.CHANGESET_UPDATE_UNREADS:
        UnreadActions.loadUnreads();
        break;
      default:
        break;
    }
  }
}

export default new UnreadStore();



// WEBPACK FOOTER //
// ./src/stores/UnreadStore.js